import React, { useState } from 'react';
import { BookmarkPlus, X, Facebook, Instagram, Link2, Check } from 'lucide-react';

const GameResult = ({ game, onSpinAgain }) => {
  const [copied, setCopied] = useState(false);

  const buildShareUrl = () => {
    const origin = window.location.origin;
    return game?.id ? `${origin}/?game=${game.id}` : window.location.href;
  };

  const copyShareLink = async () => {
    const url = buildShareUrl();
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for older browsers / insecure contexts
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_) { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const handleShare = (platform) => {
    const text = `Check out ${game.title} on Backlog Killer!`;
    const url = buildShareUrl();

    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'copy':
        copyShareLink();
        break;
      default:
        break;
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 mt-12 mb-20 animate-fade-in" data-testid="game-result">
      <div className="text-center mb-6">
        <p className="text-base sm:text-lg font-bold text-gray-500 italic mb-2">you should play</p>
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-white mb-6 sm:mb-8 break-words px-2" data-testid="game-title">
          {game.title}
        </h1>
      </div>

      <div className="bg-gradient-to-b from-gray-900 to-gray-950 border-2 border-teal-900 rounded-lg p-4 sm:p-6 md:p-8 shadow-2xl">
        <div className="flex flex-col md:flex-row gap-6 sm:gap-8">
          {/* Game Image */}
          <div className="w-full md:w-1/3 flex-shrink-0">
            <div className="relative max-w-sm mx-auto md:max-w-none">
              <img
                src={game.image}
                alt={game.title}
                className="w-full h-auto rounded shadow-lg"
              />
              <button
                className="absolute top-3 right-3 bg-gray-950/80 hover:bg-gray-950 p-2 rounded border border-gray-700 transition-all"
                onClick={() => alert('Added to list!')}
                data-testid="bookmark-button"
              >
                <BookmarkPlus className="w-5 h-5 text-yellow-500" />
              </button>
            </div>
          </div>

          {/* Game Info */}
          <div className="w-full md:w-2/3 flex flex-col">
            <div className="flex flex-wrap gap-2 sm:gap-3 mb-4">
              <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-teal-900/40 border border-teal-700 rounded text-sm sm:text-base text-teal-300 font-bold">
                {game.system}
              </span>
              <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-purple-900/40 border border-purple-700 rounded text-sm sm:text-base text-purple-300 font-bold">
                {game.genre}
              </span>
              <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-900/40 border border-yellow-700 rounded text-sm sm:text-base text-yellow-300 font-bold">
                {game.year}
              </span>
              <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-orange-900/40 border border-orange-700 rounded text-sm sm:text-base text-orange-300 font-bold italic">
                {game.vibe}
              </span>
            </div>

            <div className="text-gray-300 leading-relaxed mb-6 flex-grow text-base sm:text-lg font-medium">
              {game.description.split('. ').map((sentence, idx) => (
                <p key={idx} className="mb-3">{sentence}{idx < game.description.split('. ').length - 1 ? '.' : ''}</p>
              ))}
            </div>

            <a
              href={game.ebayLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ebay-link"
              className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-gray-950 font-black py-4 sm:py-5 px-4 sm:px-6 rounded-lg transition-all duration-300 transform hover:scale-[1.02] shadow-lg flex items-center justify-center gap-2 text-lg sm:text-xl"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 16 16">
                <path d="M2.97 1.35A1 1 0 0 1 3.73 1h8.54a1 1 0 0 1 .76.35l2.609 3.044A1.5 1.5 0 0 1 16 5.37v.255a2.375 2.375 0 0 1-4.25 1.458A2.371 2.371 0 0 1 9.875 8 2.37 2.37 0 0 1 8 7.083 2.37 2.37 0 0 1 6.125 8a2.37 2.37 0 0 1-1.875-.917A2.375 2.375 0 0 1 0 5.625V5.37a1.5 1.5 0 0 1 .361-.976l2.61-3.045zm1.78 4.275a1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0 1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0 1.375 1.375 0 1 0 2.75 0V5.37a.5.5 0 0 0-.12-.325L12.27 2H3.73L1.12 5.045A.5.5 0 0 0 1 5.37v.255a1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0zM1.5 8.5A.5.5 0 0 1 2 9v6h1v-5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v5h6V9a.5.5 0 0 1 1 0v6h.5a.5.5 0 0 1 0 1H.5a.5.5 0 0 1 0-1H1V9a.5.5 0 0 1 .5-.5zM4 15h3v-5H4v5zm5-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-3zm3 0h-2v3h2v-3z" />
              </svg>
              buy it on eBay
            </a>

            {/* Share my pick — copies a deep link to this game */}
            <button
              onClick={copyShareLink}
              data-testid="share-my-pick-button"
              className={`mt-3 w-full font-black py-3 sm:py-4 px-4 sm:px-6 rounded-lg transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-2 text-base sm:text-lg border-2 ${
                copied
                  ? 'bg-teal-900/40 border-teal-600 text-teal-300'
                  : 'bg-transparent border-teal-700 hover:border-teal-500 text-teal-300 hover:bg-teal-900/20'
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5" />
                  <span>link copied!</span>
                </>
              ) : (
                <>
                  <Link2 className="w-5 h-5" />
                  <span>share my pick</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Social Share Buttons */}
      <div className="flex justify-center items-center gap-3 sm:gap-4 mt-8">
        <button
          onClick={() => handleShare('twitter')}
          data-testid="share-twitter-button"
          aria-label="Share on Twitter"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-gray-700 hover:border-gray-500 flex items-center justify-center transition-all hover:scale-110"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
        </button>
        <button
          onClick={() => handleShare('facebook')}
          data-testid="share-facebook-button"
          aria-label="Share on Facebook"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-gray-700 hover:border-gray-500 flex items-center justify-center transition-all hover:scale-110"
        >
          <Facebook className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
        </button>
        <button
          onClick={() => alert('Instagram sharing')}
          data-testid="share-instagram-button"
          aria-label="Share on Instagram"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-gray-700 hover:border-gray-500 flex items-center justify-center transition-all hover:scale-110"
        >
          <Instagram className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
        </button>
        <button
          onClick={() => handleShare('copy')}
          data-testid="share-copy-button"
          aria-label="Copy link"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-gray-700 hover:border-gray-500 flex items-center justify-center transition-all hover:scale-110"
        >
          <Link2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
        </button>
      </div>
    </div>
  );
};

export default GameResult;
